/** this slice pertains to the sending of data to our pixel */
import * as UUID from 'uuid';
import {
  createSlice,
  createAsyncThunk,
  AsyncThunkAction,
} from '@reduxjs/toolkit';
import {
  SessionInitResponse,
  LogData,
  CommonLogData,
  OptionalSessionData,
  SessionData,
  loggingClient,
} from '@utils/loggingClient';
import { addNewNotification } from '@slices/gameStateSlice';
import { RootState } from '../store';
import { contentHash } from '@utils/hashUtils';

export type LoggingState = {
  loading: 'idle' | 'pending' | 'fulfilled' | 'rejected';
  sessionId: string | null;
  /** every request increments this count */
  logStepCounter: number;
  /** captured answer history for Fireball handoff */
  answerHistory: Array<{
    questionNameHash: string;
    answerText: string;
  }>;
  /** recruitment platform tracking code for campaign completion */
  trackingCode: string | null;
};

export const initialLoggingState: LoggingState = {
  loading: 'idle',
  sessionId: null,
  logStepCounter: 0,
  answerHistory: [],
  trackingCode: null,
};

export const startNewSession = createAsyncThunk(
  'logging/startNewSession',
  async (data: OptionalSessionData, { dispatch, rejectWithValue }) => {
    const timeoutId = setTimeout(() => {
      dispatch(
        addNewNotification(
          'Things are taking longer than usual...please try again',
        ),
      );
      rejectWithValue('session timeout error');
    }, 10000); // wait 10 sec
    try {
      const response = await loggingClient.initSession(data);
      return response;
    } catch (e) {
      dispatch(addNewNotification('Woah something is wrong!'));
      return rejectWithValue('session server error');
    } finally {
      clearTimeout(timeoutId);
    }
  },
);

export const startNewPairShareSession = (
  data: OptionalSessionData,
): AsyncThunkAction<
  SessionInitResponse,
  Partial<SessionData>,
  Record<string, unknown>
> =>
  startNewSession({ ...data, user_id: UUID.v4(), is_pair_share_session: true });

export const logItem = createAsyncThunk<
  // Return type of the payload creator
  void,
  LogData & CommonLogData,
  {
    state: RootState;
  }
>('logging/logItem', async (logData, { getState }) => {
  const state = getState();
  await loggingClient.log(
    {
      ...logData,
      step_counter: state.logging.logStepCounter,
    },
    state.logging.sessionId,
  );
});

export const batchLogItem = createAsyncThunk<
  // Return type of the payload creator
  void,
  // here particularly, we want the question name as you may be batching multiple items of different names
  (LogData & { question_name: string })[],
  {
    state: RootState;
  }
>('logging/batchLogItem', async (logData, { getState }) => {
  const state = getState();
  // go through each batch and add the correct step counter
  // we use the same step for each
  const countedLogData = logData.map(e => ({
    ...e,
    step_counter: state.logging.logStepCounter,
  }));
  await loggingClient.batchLog(countedLogData, state.logging.sessionId);
});

const incrementCounter = (state: LoggingState) => {
  state.logStepCounter += 1;
};

export const loggingSlice = createSlice({
  name: 'logging',
  initialState: initialLoggingState,
  reducers: {
    // used when we find an id on the users ls and need to set it (shorting the session init step)
    forceSetSessionId: (state, action) => {
      const id = action.payload;
      state.sessionId = id;
    },
    setTrackingCode: (state, action) => {
      state.trackingCode = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(startNewSession.fulfilled, (state, action) => {
        // this is stored only during the games runtime
        state.sessionId = action.payload.sessionId;
      })
      .addCase(logItem.pending, (state, action) => {
        // Capture answer data for Fireball handoff
        const payload = action.meta.arg;

        if (
          payload.collection_name === 'answers' &&
          payload.question_name &&
          payload.answer_text
        ) {
          const questionNameHash = contentHash(payload.question_name);
          console.log(
            `Hashing: "${payload.question_name}": ${questionNameHash}`,
          );
          state.answerHistory.push({
            questionNameHash,
            answerText: payload.answer_text,
          });
        }
      })
      .addCase(logItem.fulfilled, incrementCounter)
      .addCase(batchLogItem.fulfilled, incrementCounter)
      .addCase('gameState/resetGame', () => ({
        ...initialLoggingState,
      }));
  },
});

export const { forceSetSessionId, setTrackingCode } = loggingSlice.actions;

export default loggingSlice.reducer;
